import { CKO2026_TRICKS } from './constants';
import type {
  Cko2026WarmupLeaderboardEntry,
  Cko2026WarmupProfile,
  Cko2026WarmupQueueStatus,
  Cko2026WarmupRemovalNotice,
  Cko2026WarmupSubmission,
  Cko2026WarmupUploadConfig,
  TournamentTrick
} from './types';

export const CKO2026_WARMUP_MODE_NAME = 'CKO2026预热赛';
export const CKO2026_WARMUP_MAX_VIDEO_SIZE = 20 * 1024 * 1024;
export const CKO2026_WARMUP_REMOTE_SOURCE_MAX_VIDEO_SIZE = 300 * 1024 * 1024;

const isLocalBackend = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

const trickOrderMap = new Map(CKO2026_TRICKS.map((trick, index) => [trick.id, index]));
const canonicalTrickNameMap = new Map(CKO2026_TRICKS.map((trick) => [trick.id, trick.name]));

const normalizeWarmupVideoUrl = (rawUrl: string) => {
  const trimmedUrl = rawUrl.trim();
  if (!trimmedUrl || typeof window === 'undefined') {
    return trimmedUrl;
  }

  if (trimmedUrl.startsWith('blob:') || trimmedUrl.startsWith('data:')) {
    return trimmedUrl;
  }

  try {
    const parsedUrl = new URL(trimmedUrl, window.location.href);
    const isUploadPath = parsedUrl.pathname.includes('/wp-content/uploads/')
      || parsedUrl.pathname.includes('/uploads/');

    if (window.location.protocol === 'https:' && parsedUrl.protocol === 'http:' && isUploadPath) {
      return `${window.location.origin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    return parsedUrl.toString();
  } catch {
    return trimmedUrl;
  }
};

const normalizeSubmission = (submission: any): Cko2026WarmupSubmission | null => {
  const trickId = String(submission?.trickId || '').trim();
  const videoUrl = normalizeWarmupVideoUrl(String(submission?.videoUrl || ''));
  const trickName = canonicalTrickNameMap.get(trickId) || String(submission?.trickName || '').trim();
  const score = Number(submission?.score);
  const uploadedAt = Number(submission?.uploadedAt);
  const status = typeof submission?.status === 'string' ? submission.status : (videoUrl ? 'ready' : 'processing');

  if (!trickId || !trickName || !Number.isFinite(score) || !Number.isFinite(uploadedAt)) {
    return null;
  }

  if (status === 'ready' && !videoUrl) {
    return null;
  }

  return {
    trickId,
    trickName,
    score,
    videoUrl: videoUrl || undefined,
    fileName: typeof submission?.fileName === 'string' ? submission.fileName : undefined,
    mimeType: typeof submission?.mimeType === 'string' ? submission.mimeType : undefined,
    sourceUrl: typeof submission?.sourceUrl === 'string' ? normalizeWarmupVideoUrl(submission.sourceUrl) : undefined,
    sourceFileName: typeof submission?.sourceFileName === 'string' ? submission.sourceFileName : undefined,
    sourceMimeType: typeof submission?.sourceMimeType === 'string' ? submission.sourceMimeType : undefined,
    status,
    processingMessage: typeof submission?.processingMessage === 'string'
      ? decodeWarmupEscapedText(submission.processingMessage)
      : undefined,
    uploadedAt,
    likes: Number.isFinite(Number(submission?.likes)) ? Number(submission.likes) : 0,
    likedBy: Array.isArray(submission?.likedBy) ? submission.likedBy : [],
    reported: Boolean(submission?.reported),
    reportedBy: typeof submission?.reportedBy === 'string' ? submission.reportedBy : undefined,
    reportedByDisplayName: typeof submission?.reportedByDisplayName === 'string' ? submission.reportedByDisplayName : undefined,
    reportedAt: Number.isFinite(Number(submission?.reportedAt)) ? Number(submission.reportedAt) : undefined,
    reportReason: typeof submission?.reportReason === 'string'
      ? decodeWarmupEscapedText(submission.reportReason)
      : undefined,
  };
};

const normalizeRemovedNotice = (notice: any): Cko2026WarmupRemovalNotice | null => {
  const trickId = String(notice?.trickId || '').trim();
  const trickName = String(notice?.trickName || '').trim();
  const score = Number(notice?.score);
  const reason = String(notice?.reason || '').trim();
  const removedAt = Number(notice?.removedAt);
  const noticeType = notice?.noticeType === 'prompt' ? 'prompt' : 'removal';

  if (!trickId || !trickName || !Number.isFinite(score) || !Number.isFinite(removedAt) || removedAt <= 0) {
    return null;
  }

  return {
    trickId,
    trickName,
    score,
    reason,
    removedAt,
    noticeType,
  };
};

const sortSubmissions = (submissions: Cko2026WarmupSubmission[]) =>
  [...submissions].sort((a, b) => {
    const aIndex = trickOrderMap.get(a.trickId) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = trickOrderMap.get(b.trickId) ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex || b.uploadedAt - a.uploadedAt;
  });

export const createEmptyCko2026WarmupProfile = (): Cko2026WarmupProfile => ({
  modeName: CKO2026_WARMUP_MODE_NAME,
  isRegistered: false,
  registeredAt: null,
  submissions: []
});

export const normalizeCko2026WarmupUploadConfig = (config: unknown): Cko2026WarmupUploadConfig => {
  const raw = config && typeof config === 'object' ? (config as Record<string, unknown>) : {};
  return {
    uploadsEnabled: typeof raw.uploadsEnabled === 'boolean' ? raw.uploadsEnabled : true,
    updatedAt: Number.isFinite(Number(raw.updatedAt)) ? Number(raw.updatedAt) : 0
  };
};

export const normalizeCko2026WarmupProfile = (profile: unknown): Cko2026WarmupProfile => {
  const raw = profile && typeof profile === 'object' ? (profile as Record<string, unknown>) : {};
  const submissions = Array.isArray(raw.submissions)
    ? raw.submissions.map(normalizeSubmission).filter((item): item is Cko2026WarmupSubmission => !!item)
    : [];

  const dedupedMap = new Map<string, Cko2026WarmupSubmission>();
  submissions.forEach((submission) => {
    const existing = dedupedMap.get(submission.trickId);
    if (!existing || submission.uploadedAt >= existing.uploadedAt) {
      dedupedMap.set(submission.trickId, submission);
    }
  });

  const registeredAtValue = raw.registeredAt;
  const registeredAt = Number.isFinite(Number(registeredAtValue)) ? Number(registeredAtValue) : null;
  const isRegistered = Boolean(raw.isRegistered) || !!registeredAt || dedupedMap.size > 0;

  return {
    modeName: typeof raw.modeName === 'string' && raw.modeName.trim() ? raw.modeName : CKO2026_WARMUP_MODE_NAME,
    isRegistered,
    registeredAt,
    submissions: sortSubmissions(Array.from(dedupedMap.values())),
    removedNotices: Array.isArray(raw.removedNotices)
      ? raw.removedNotices.map(normalizeRemovedNotice).filter((item): item is Cko2026WarmupRemovalNotice => !!item)
      : [],
  };  
};

export const getCko2026WarmupTotalScore = (profile?: Cko2026WarmupProfile | null) =>
  normalizeCko2026WarmupProfile(profile).submissions.reduce(
    (sum, submission) => sum + ((submission.status ?? 'ready') === 'ready' ? submission.score : 0),
    0
  );

export const getCko2026WarmupCompletedCount = (profile?: Cko2026WarmupProfile | null) =>
  normalizeCko2026WarmupProfile(profile).submissions.filter((submission) => (submission.status ?? 'ready') === 'ready').length;

export const groupCko2026WarmupTricksByScore = () => {
  const groups = new Map<number, TournamentTrick[]>();
  CKO2026_TRICKS.forEach((trick) => {
    if (trick.score < 1) return; // skip 0.5-level tricks for warmup
    const list = groups.get(trick.score) ?? [];
    list.push(trick);
    groups.set(trick.score, list);
  });

  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([score, tricks]) => ({ score, tricks }));
};

const parseJsonResponse = async (response: Response) => {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    throw new Error(`Non-JSON response (${response.status})`);
  }
};

const getApiUrl = (path: string, action: string) => {
  if (isLocalBackend()) {
    return path;
  }
  if (typeof window === 'undefined') {
    return `/myapp/api.php?action=${encodeURIComponent(action)}`;
  }
  const url = new URL('/myapp/api.php', window.location.origin);
  url.searchParams.set('action', action);
  return url.toString();
};

const decodeWarmupEscapedText = (value: string) => {
  if (!value) return value;

  if (!/(?:\\u|u)[0-9a-fA-F]{4}/.test(value)) {
    return value;
  }

  let decoded = value;

  // Some legacy records lost part or all of the backslashes in `\uXXXX` escapes.
  // Decode repeatedly so mixed forms like `\u5169u5708u5f8c...` fully recover.
  for (let i = 0; i < 3; i += 1) {
    const next = decoded.replace(/\\?u([0-9a-fA-F]{4})/g, (_, hex: string) =>
      String.fromCharCode(parseInt(hex, 16))
    );
    if (next === decoded) {
      break;
    }
    decoded = next;
  }

  return decoded;
};

const WARMUP_VIDEO_EXTENSION_MIME_MAP: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  ogv: 'video/ogg',
  ogg: 'video/ogg',
  mkv: 'video/x-matroska'
};

const inferWarmupVideoMimeType = (file: File) => {
  const normalizedType = file.type.split(';')[0].trim().toLowerCase();
  if (normalizedType.startsWith('video/')) {
    return normalizedType;
  }

  const fileName = String(file.name || '').trim().toLowerCase();
  const ext = fileName.includes('.') ? fileName.split('.').pop() || '' : '';
  return WARMUP_VIDEO_EXTENSION_MIME_MAP[ext] || '';
};

const normalizeWarmupVideoDataUrl = (dataUrl: string, mimeType: string) => {
  if (!mimeType || /^data:video\/[a-z0-9.+-]+(?:;[^;]*)*;base64,/i.test(dataUrl)) {
    return dataUrl;
  }
  return dataUrl.replace(/^data:(?:[^;]*)?(?:;[^;]*)*;base64,/i, `data:${mimeType};base64,`);
};

export const fetchCko2026WarmupLeaderboard = async (): Promise<Cko2026WarmupLeaderboardEntry[]> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/leaderboard', 'cko2026_warmup_leaderboard'),
    { credentials: 'include' }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || '排行榜获取失败');
  }

  return Array.isArray(data?.leaderboard)
    ? data.leaderboard.map((entry: any) => ({
        userId: String(entry?.userId || ''),
        displayName: String(entry?.displayName || 'Unknown Player'),
        avatarUrl: typeof entry?.avatarUrl === 'string' ? entry.avatarUrl : undefined,
        totalScore: Number(entry?.totalScore) || 0,
        completedCount: Number(entry?.completedCount) || 0,
        registeredAt: Number.isFinite(Number(entry?.registeredAt)) ? Number(entry.registeredAt) : null,
        updatedAt: Number.isFinite(Number(entry?.updatedAt)) ? Number(entry.updatedAt) : null,
        submissions: sortSubmissions(
          Array.isArray(entry?.submissions)
            ? entry.submissions.map(normalizeSubmission).filter((item: Cko2026WarmupSubmission | null): item is Cko2026WarmupSubmission => !!item)
            : []
        )
      }))
    : [];
};

export const fetchCko2026WarmupLeaderboardEntry = async (userId: string): Promise<Cko2026WarmupLeaderboardEntry> => {
  const apiUrl = getApiUrl('/api/cko2026/warmup/leaderboard', 'cko2026_warmup_leaderboard');
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetUserId: userId })
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || data?.success === false || !data?.entry) {
    throw new Error(data?.error || '选手详情获取失败');
  }

  const entry = data.entry;
  return {
    userId: String(entry?.userId || ''),
    displayName: String(entry?.displayName || 'Unknown Player'),
    avatarUrl: typeof entry?.avatarUrl === 'string' ? entry.avatarUrl : undefined,
    totalScore: Number(entry?.totalScore) || 0,
    completedCount: Number(entry?.completedCount) || 0,
    registeredAt: Number.isFinite(Number(entry?.registeredAt)) ? Number(entry.registeredAt) : null,
    updatedAt: Number.isFinite(Number(entry?.updatedAt)) ? Number(entry.updatedAt) : null,
    submissions: sortSubmissions(
      Array.isArray(entry?.submissions)
        ? entry.submissions.map(normalizeSubmission).filter((item: Cko2026WarmupSubmission | null): item is Cko2026WarmupSubmission => !!item)
        : []
    )
  };
};

export const registerForCko2026Warmup = async (): Promise<Cko2026WarmupProfile> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/register', 'cko2026_warmup_register'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({})
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '报名失败');
  }
  return normalizeCko2026WarmupProfile(data?.profile);
};

export const fetchMyCko2026WarmupProfile = async (): Promise<Cko2026WarmupProfile> => {
  const response = await fetch(getApiUrl('/api/me', 'me'), { credentials: 'include' });
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '获取预热赛状态失败');
  }
  return normalizeCko2026WarmupProfile(data?.user?.cko2026Warmup);
};

export const fetchCko2026WarmupUploadConfig = async (): Promise<Cko2026WarmupUploadConfig> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/upload-config', 'cko2026_warmup_upload_config'),
    { credentials: 'include' }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '获取预热赛上传设置失败');
  }
  return normalizeCko2026WarmupUploadConfig(data?.config);
};

export const saveCko2026WarmupUploadConfig = async (uploadsEnabled: boolean): Promise<Cko2026WarmupUploadConfig> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/admin/upload-config', 'cko2026_warmup_admin_save_upload_config'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        config: {
          uploadsEnabled,
          updatedAt: Date.now()
        }
      })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '保存预热赛上传设置失败');
  }
  return normalizeCko2026WarmupUploadConfig(data?.config);
};

export const likeCko2026WarmupSubmission = async (targetUserId: string, trickId: string): Promise<{ likes: number }> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/submission/like', 'cko2026_warmup_like'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ targetUserId, trickId })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '点赞失败');
  }
  return { likes: data.likes };
};

export const reportCko2026WarmupSubmission = async (targetUserId: string, trickId: string, reason: string): Promise<void> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/submission/report', 'cko2026_warmup_report'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ targetUserId, trickId, reason })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '举报失败');
  }
};

export const unreportCko2026WarmupSubmission = async (targetUserId: string, trickId: string): Promise<void> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/submission/unreport', 'cko2026_warmup_unreport'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ targetUserId, trickId })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '取消举报失败');
  }
};

export interface Cko2026WarmupReportEntry {
  userId: string;
  displayName: string;
  avatarUrl?: string;
  submission: Cko2026WarmupSubmission;
}

export const fetchCko2026WarmupReports = async (): Promise<Cko2026WarmupReportEntry[]> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/admin/reports', 'cko2026_warmup_admin_reports'),
    { credentials: 'include' }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '获取举报列表失败');
  }
  return Array.isArray(data?.reports)
    ? data.reports.map((entry: any) => ({
        userId: String(entry?.userId || ''),
        displayName: String(entry?.displayName || ''),
        avatarUrl: typeof entry?.avatarUrl === 'string' ? entry.avatarUrl : undefined,
        submission: normalizeSubmission(entry?.submission)!
      }))
    : [];
};

export const removeCko2026WarmupSubmission = async (targetUserId: string, trickId: string, reason: string): Promise<void> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/admin/remove', 'cko2026_warmup_admin_remove'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ targetUserId, trickId, reason })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '移除失败');
  }
};

export const notifyCko2026WarmupSubmission = async (targetUserId: string, trickId: string, reason: string): Promise<void> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/admin/notify', 'cko2026_warmup_admin_notify'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ targetUserId, trickId, reason })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '发送提示失败');
  }
};

export const resolveInvalidCko2026WarmupReport = async (targetUserId: string, trickId: string): Promise<void> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/admin/resolve-report', 'cko2026_warmup_admin_resolve_report'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ targetUserId, trickId })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '标记举报无效失败');
  }
};

export const deleteOwnCko2026WarmupSubmission = async (trickId: string): Promise<Cko2026WarmupProfile> => {
  const response = await fetch(
    getApiUrl('/api/cko2026/warmup/submission/delete', 'cko2026_warmup_delete'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ trickId })
    }
  );
  const data = await parseJsonResponse(response);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '删除视频失败');
  }
  return normalizeCko2026WarmupProfile(data?.profile);
};
const UPLOAD_TIMEOUT_MS = 300_000;

const xhrUpload = (
  url: string,
  method: string,
  headers: Record<string, string> | null,
  body: Document | XMLHttpRequestBodyInit | null,
  onProgress: ((pct: number) => void) | undefined,
  timeoutMs: number
): Promise<{ ok: boolean; status: number; text: () => Promise<string> }> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.timeout = timeoutMs;
    xhr.withCredentials = true;

    if (headers) {
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    }

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.min(99, Math.round((e.loaded / e.total) * 100));
          onProgress(pct);
        }
      };
    }

    xhr.onload = () => {
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        text: () => Promise.resolve(xhr.responseText)
      });
    };

    xhr.onerror = () => reject(new Error('网络错误，请检查网络连接后重试。'));
    xhr.ontimeout = () => reject(new Error('上传超时，请检查网络后重试。'));

    xhr.send(body);
  });

const readWarmupVideoAsDataUrl = (file: File, mimeType: string) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('视频读取失败，请重新选择文件后重试。'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result) {
        reject(new Error('视频读取失败，请重新选择文件后重试。'));
        return;
      }
      resolve(normalizeWarmupVideoDataUrl(result, mimeType));
    };
    reader.readAsDataURL(file);
  });

const uploadCko2026WarmupVideoDirectly = async (
  trick: TournamentTrick,
  file: File,
  onProgress?: (pct: number) => void
): Promise<Cko2026WarmupProfile> => {
  const uploadMimeType = inferWarmupVideoMimeType(file) || 'video/webm';
  const apiUrl = getApiUrl('/api/cko2026/warmup/submission', 'cko2026_warmup_submit');
  const useMultipartUpload = !isLocalBackend();
  let requestBody: Document | XMLHttpRequestBodyInit | null = null;
  let requestHeaders: Record<string, string> | null = null;

  if (useMultipartUpload) {
    const formData = new FormData();
    formData.append('trickId', trick.id);
    formData.append('trickName', trick.name);
    formData.append('score', String(trick.score));
    formData.append('fileName', file.name);
    formData.append('mimeType', uploadMimeType);
    formData.append('video', file, file.name);
    requestBody = formData;
  } else {
    const videoDataUrl = await readWarmupVideoAsDataUrl(file, uploadMimeType);
    requestBody = JSON.stringify({
      trickId: trick.id,
      trickName: trick.name,
      score: trick.score,
      fileName: file.name,
      mimeType: uploadMimeType,
      videoDataUrl
    });
    requestHeaders = { 'Content-Type': 'application/json' };
  }

  const response = await xhrUpload(
    apiUrl,
    'POST',
    requestHeaders,
    requestBody,
    onProgress,
    UPLOAD_TIMEOUT_MS
  );

  const responseText = await response.text();
  const data = await parseJsonResponse({ text: () => Promise.resolve(responseText), status: response.status } as any);
  if (!response.ok || !data?.success) {
    throw new Error(data?.error || '视频上传失败');
  }

  if (onProgress) {
    onProgress(100);
  }
  return normalizeCko2026WarmupProfile(data?.profile);
};

const uploadCko2026WarmupVideoInChunks = async (
  trick: TournamentTrick,
  file: File,
  onProgress?: (pct: number) => void
): Promise<Cko2026WarmupProfile> => {
  const uploadMimeType = inferWarmupVideoMimeType(file) || 'video/webm';
  const initApiUrl = getApiUrl('/api/cko2026/warmup/submission/direct-init', 'cko2026_warmup_submit_direct_init');
  const completeApiUrl = getApiUrl('/api/cko2026/warmup/submission/direct-complete', 'cko2026_warmup_submit_direct_complete');
  const initResponse = await fetch(initApiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      trickId: trick.id,
      trickName: trick.name,
      score: trick.score,
      fileName: file.name,
      mimeType: uploadMimeType,
      fileSize: file.size
    })
  });
  const initData = await parseJsonResponse(initResponse);
  if (!initResponse.ok || !initData?.success || typeof initData?.putUrl !== 'string' || typeof initData?.uploadId !== 'string') {
    throw new Error(initData?.error || '初始化 OSS 上传失败');
  }

  const directUploadHeaders =
    initData?.headers && typeof initData.headers === 'object'
      ? Object.fromEntries(
          Object.entries(initData.headers as Record<string, unknown>)
            .filter((entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string')
        )
      : { 'Content-Type': uploadMimeType };

  const uploadResponse = await xhrUpload(
    initData.putUrl,
    'PUT',
    directUploadHeaders,
    file,
    onProgress ? (pct) => onProgress(Math.min(95, Math.max(1, pct))) : undefined,
    UPLOAD_TIMEOUT_MS
  );
  if (!uploadResponse.ok) {
    throw new Error('直传 OSS 失败，请稍后重试。');
  }

  const completeResponse = await fetch(completeApiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      uploadId: initData.uploadId
    })
  });
  const completeData = await parseJsonResponse(completeResponse);
  if (!completeResponse.ok || !completeData?.success) {
    throw new Error(completeData?.error || '上传已完成，但创建转码任务失败');
  }

  if (onProgress) {
    onProgress(100);
  }
  return normalizeCko2026WarmupProfile(completeData?.profile);
};

export const uploadCko2026WarmupVideo = async (
  trick: TournamentTrick,
  file: File,
  onProgress?: (pct: number) => void
): Promise<Cko2026WarmupProfile> =>
  isLocalBackend()
    ? uploadCko2026WarmupVideoDirectly(trick, file, onProgress)
    : uploadCko2026WarmupVideoInChunks(trick, file, onProgress);

export const fetchCko2026WarmupQueueStatus = async (): Promise<Cko2026WarmupQueueStatus | null> => {
  try {
    const apiUrl = getApiUrl('/api/me', 'cko2026_warmup_queue_status');
    const res = await fetch(apiUrl, { credentials: 'include' });
    const data = await parseJsonResponse(res as any);
    if (!data?.success || !data?.queue) return null;
    return data.queue as Cko2026WarmupQueueStatus;
  } catch {
    return null;
  }
};
